import React from 'react';
import { Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography, IconButton, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { RangeChips, sectionCardSx, sectionTitleSx } from './dashboardStyles';

const CHAT_RANGES = [
    { key: 'last', label: 'Last chat' },
    { key: 'week', label: 'This week' },
    { key: 'month', label: 'This month' },
    { key: 'custom', label: 'Custom' },
];

// Shared presentation only. The live container supplies its existing handlers;
// onboarding supplies example data and inert/no-op handlers. No data hooks here.
export default function DashboardChatSections({ chatCustomFrom, chatCustomTo, chatRange, chatSummariesRef, filteredChatSessions, formatDate, getRiskStyles, loadingConversation, openSession, openTranscriptSessionId, selectedConversation, selectedSessionId, setChatAppliedFrom, setChatAppliedTo, setChatCustomFrom, setChatCustomTo, setChatRange, setOpenTranscriptSessionId, setSelectedSessionId }) {
  return (<Grid container spacing={3} ref={chatSummariesRef} alignItems="stretch">
            <Grid
                size={{ xs: 12, md: openSession ? 7 : 12 }}
                sx={{ display: 'flex' }}
            >
            <Paper
            data-tour="demo-dashboard-chats"
            elevation={0}
            sx={{ ...sectionCardSx, height: '100%', width: '100%' }}
            >
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={sectionTitleSx}>
                Chat Summaries
                </Typography>

                <RangeChips options={CHAT_RANGES} value={chatRange} label="Chat summary period" onChange={(key) => {
                    setChatRange(key);
                    if (key === 'custom') {
                        setChatCustomFrom('');
                        setChatCustomTo('');
                        setChatAppliedFrom('');
                        setChatAppliedTo('');
                    }
                }} />
            </Stack>

            {chatRange === 'custom' && (
            <Box sx={{ mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                <TextField
                    type="date"
                    size="small"
                    label="Start date"
                    value={chatCustomFrom}
                    onChange={(e) => setChatCustomFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    type="date"
                    size="small"
                    label="End date"
                    value={chatCustomTo}
                    onChange={(e) => setChatCustomTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                    setChatAppliedFrom(chatCustomFrom);
                    setChatAppliedTo(chatCustomTo);
                    }}
                    sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#29C0BB',
                    }}
                >
                    Apply
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                    setChatCustomFrom('');
                    setChatCustomTo('');
                    setChatAppliedFrom('');
                    setChatAppliedTo('');
                    }}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                    Reset
                </Button>
                </Stack>
            </Box>
            )}

            <Stack spacing={3}>
                {filteredChatSessions.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No chat sessions found.
                </Typography>
                )}

                {filteredChatSessions.map((session) => (
                <Box
                    key={session.id ?? session.session_id}
                    onClick={() => setSelectedSessionId(session.session_id)}
                    sx={{
                    borderLeft: '4px solid #29C0BB',
                    pl: 2,
                    py: 1,
                    cursor: 'pointer',
                    backgroundColor:
                        selectedSessionId === session.session_id ? '#F5F7FA' : 'transparent',
                    borderRadius: 1,
                    }}
                >
                    {session.risk_level && (
                    <Box
                        sx={{
                        mb: 1,
                        p: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: getRiskStyles(session.risk_level).bg,
                        border: `1px solid ${getRiskStyles(session.risk_level).border}`,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: getRiskStyles(session.risk_level).text }}
                        >
                            Crisis Alert:
                        </Typography>
                        <Chip
                            label={session.risk_level}
                            size="small"
                            sx={{
                            backgroundColor: '#ffffff',
                            color: getRiskStyles(session.risk_level).text,
                            border: `1px solid ${getRiskStyles(session.risk_level).border}`,
                            fontWeight: 600,
                            }}
                        />
                        {/* {session.risk_confidence != null && (
                            <Chip
                            label={`Confidence: ${session.risk_confidence}`}
                            size="small"
                            sx={{
                                backgroundColor: '#ffffff',
                                color: getRiskStyles(session.risk_level).text,
                                border: `1px solid ${getRiskStyles(session.risk_level).border}`,
                            }}
                            />
                        )} */}
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280' }}>
                            Risks:
                        </Typography>
                        {(Array.isArray(session.risk_signals) ? session.risk_signals : []).map(
                            (signal, idx) => (
                            <Chip
                                key={`${signal}-${idx}`}
                                label={signal}
                                size="small"
                                sx={{
                                    backgroundColor: '#ffffff',
                                    color: getRiskStyles(session.risk_level).text,
                                    border: `1px solid ${getRiskStyles(session.risk_level).border}`,
                                    fontWeight: 600,
                                }}
                            />
                            )
                        )}
                        </Stack>
                    </Box>
                    )}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {formatDate(session.created_at)}
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="View chat transcript"
                        onClick={(e) => {
                        e.stopPropagation();
                        if (openTranscriptSessionId === session.session_id) {
                            setOpenTranscriptSessionId(null);
                        } else {
                            setOpenTranscriptSessionId(session.session_id);
                            setSelectedSessionId(session.session_id);
                        }
                        }}
                        sx={{ color: '#29C0BB' }}
                    >
                        <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    </Stack>

                    {(session.emotions || []).length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip
                        label={`Emotion:  ${session.emotions.join(', ')}`}
                        size="small"
                        sx={{ backgroundColor: '#FFEAD5', color: '#C05621' }}
                        />
                    </Stack>
                    )}

                    <Stack
                        direction="row"
                        spacing={0}
                        flexWrap="wrap"
                        sx={{
                            mt: 1,
                            justifyContent: 'flex-start',
                            rowGap: 1,
                        }}
                        >
                        {(session.key_themes || []).map((t, idx) => (
                            <Chip key={`${t}-${idx}`} label={t} size="small" sx={{ mr: 1 }} />
                        ))}
                    </Stack>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {session.session_summary || 'No summary available.'}
                    </Typography>

                    {/* <Button
                    variant="contained"
                    size="small"
                    sx={{
                        mt: 2,
                        backgroundColor: '#29C0BB',
                        '&:hover': { backgroundColor: '#1ca8a4' },
                        textTransform: 'none',
                    }}
                    >
                    Generate More Details
                    </Button> */}
                </Box>
                ))}
            </Stack>
            </Paper>
        </Grid>

        {openSession && (
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex' }}>
            <Paper
                elevation={0}
                sx={{
                ...sectionCardSx,
                height: '100%',
                width: '100%',
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={sectionTitleSx}>
                        Session Transcript
                    </Typography>
                    <IconButton
                        onClick={() => setOpenTranscriptSessionId(null)}
                        sx={{ color: '#9E9E9E' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {openSession?.created_at ? formatDate(openSession.created_at) : '--'}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Full Conversation Transcript
                </Typography>

                {loadingConversation ? (
                <CircularProgress size={20} />
                ) : selectedConversation.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No transcript available.
                </Typography>
                ) : (
                <Stack data-tour="demo-dashboard-conversation" spacing={2}>
                    {selectedConversation.map((msg, idx) => {
                    const isAI = msg.role === 'assistant';
                    return (
                        <Box
                        key={`${msg.role}-${idx}`}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: isAI ? '#E8F4F4' : '#F5F7FA',
                            borderLeft: `4px solid ${isAI ? '#29C0BB' : '#A0AEC0'}`,
                        }}
                        >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#4a5568' }}>
                            {isAI ? 'AI' : 'User'}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                            {msg.content}
                        </Typography>
                        </Box>
                    );
                    })}
                </Stack>
                )}
            </Paper>
            </Grid>
        )}
        </Grid>);
}
